/**
 * O token de acesso, lido de um lugar só.
 *
 * ## Por que este arquivo existe
 *
 * O projeto guarda a sessão em `localStorage["seweb_auth"]`, um JSON com
 * `access_token` dentro. Mas havia uma chave LEGADA, `access_token` solta, que
 * ninguém escreve no login — só `TenantContext.switchTenant` grava, e nada
 * limpa.
 *
 * 🔴 Medido em 31/08/2026, no console de produção:
 *
 *     GET /api/v1/auth/tenants 401 (Unauthorized)
 *
 * `TenantContext` lia a chave legada, achava um token de uma sessão antiga e
 * pedia os tenants com ele. O 401 era o servidor recusando um token expirado.
 * Pior que o erro no console: quando a chave legada **não existe**,
 * `currentTenant` nunca é definido — e `useTTSAlert` desiste em silêncio,
 * porque começa com `if (!currentTenant?.id) return`. Os alertas por voz
 * simplesmente não tocavam, sem nenhum erro.
 *
 * `PresenceContext.tsx` já avisava, em comentário: *"pattern oficial do projeto
 * é `seweb_auth` JSON com `access_token`"* e *"useChat.ts usa
 * `localStorage.getItem("access_token")` legado — não copiar"*. O aviso existia;
 * faltava o lugar único para onde apontar.
 *
 * ⚠️ `useChat.ts:362` ainda lê a chave legada. Não foi trocado junto porque o
 *    chat tem fluxo próprio de reconexão que eu não consegui exercitar — está
 *    anotado, não esquecido.
 */

/** A chave onde o login de fato grava. Ver `AuthContext.tsx`. */
export const AUTH_STORAGE_KEY = "seweb_auth";

/**
 * O JWT da sessão, ou `null`.
 *
 * Nunca lança: é chamado dentro de `useEffect` de contexto, onde uma exceção
 * derruba a árvore inteira do React. `localStorage` pode não existir (SSR,
 * teste) ou estar bloqueado (aba anônima com site data desligado).
 */
export function lerTokenDeAcesso(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const bruto = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!bruto) return null;
    const dados = JSON.parse(bruto);
    return dados?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Quando a sessão atual vence, em ms desde a época — o `expires_at` que o
 * `AuthContext` grava no login e no refresh —, ou `null`.
 *
 * Quem precisa disto é quem pergunta ao servidor sozinho, sem clique: o
 * `/premio` para de consultar ANTES de o token vencer, porque um 401 no
 * `apiClient` apaga a sessão e manda para o `/login` sem caminho de volta.
 *
 * Nunca lança, pelo mesmo motivo de `lerTokenDeAcesso`.
 */
export function lerExpiracaoDaSessao(): number | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const bruto = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!bruto) return null;
    const expira = JSON.parse(bruto)?.expires_at;
    return typeof expira === "number" && Number.isFinite(expira) ? expira : null;
  } catch {
    return null;
  }
}

/**
 * O corpo do JWT, ou `null`.
 *
 * ⚠️ Isto NÃO valida assinatura nem expiração — quem valida é o servidor. Serve
 * para ler `company_id`/`company_slug` sem uma ida à rede, e o pior caso de um
 * token adulterado aqui é a tela mostrar o nome errado da empresa até a
 * primeira chamada autenticada ser recusada.
 */
export function lerCorpoDoToken<T = Record<string, unknown>>(): T | null {
  const token = lerTokenDeAcesso();
  if (!token) return null;
  try {
    const corpo = token.split(".")[1];
    if (!corpo) return null;
    return JSON.parse(atob(corpo)) as T;
  } catch {
    return null;
  }
}
